import { useParams } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { booleanPointInPolygon } from '@turf/turf';
import { useEffect, useMemo, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { getProject as apiGetProject } from '~/api/projects2';
import type { Project } from '~/common/types';
import { useOwnMapClicks } from '~/components2/Map/mapClickOwnership';
import { measureStatus } from '~/components2/Map/Widgets/Measure/MeasureSurface';
import { GlobalFilter, Pagination, TableV2 } from '~/components2/Table';
import { useTabControlledLayers } from '~/store2/tabLayerLocks';
import type { ProjectDetailsParams } from '..';
import { columns, pagination, sorting } from './columns';
import Export from './Export';
import GetDocuments from './GetDocuments';
import GetZoningInfo from './GetZoningInfo';
import store from './store';

const PARCELS_LAYER_ID = 'landRightsParcels';
const MAPBOX_PARCELS_LAYER_ID = `${PARCELS_LAYER_ID}-all`;
// Stable identity — useTabControlledLayers re-locks on identity change.
const TAB_CONTROLLED_LAYER_IDS = [PARCELS_LAYER_ID];

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { parcelDetails, selectedIds } = useSnapshot(store) as typeof store;
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  useEffect(() => {
    store.fetchParcelDetails(projectId);
  }, [projectId]);
  const [project, setProject] = useState<Project>();

  useEffect(() => {
    (async () => {
      const p = (await apiGetProject(projectId)) as Project;
      setProject(p);
    })();
  }, [projectId]);

  const table = useReactTable({
    data: parcelDetails,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId: (p) => p.uniqueId,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    initialState: {
      pagination,
      sorting,
    },
  });

  useEffect(() => {
    store.selectedIds.clear();
    Object.keys(rowSelection).forEach((id) => {
      store.selectedIds.add(id);
    });
  }, [rowSelection]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const sel: RowSelectionState = {};
    selectedIds.forEach((id) => {
      sel[id] = true;
    });
    setRowSelection(sel);
  }, [selectedIds.size]);

  return (
    <div className="relative flex h-full w-full flex-col justify-start space-y-4 overflow-hidden">
      <div className="grid grid-cols-1 gap-2 overflow-auto">
        <TableV2
          id="land-rights-table"
          data-testid="land-rights-table"
          table={table}
          topSection={
            <div className="space-y-2 pb-2">
              <div className="flex w-full justify-end space-x-1.5">
                <GetDocuments />
                <GetZoningInfo project={project} />
                <Export project={project} table={table} disabled={!table.getCoreRowModel()?.rows?.length} />
              </div>

              <div className="flex items-end justify-between">
                <Pagination table={table} data-testid="land-rights-pagination" />

                <div>
                  <GlobalFilter table={table} data-testid="land-rights-search" />
                </div>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}

export function MapChildren() {
  // This tab uses left-click to select parcels, so claim map-click ownership (CG-670 sidebar defers).
  useOwnMapClicks();
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { parcelDetails, selectedIds } = useSnapshot(store) as typeof store;
  const { current: map } = MapGL.useMap();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    async function onClick({ lngLat }: MapGL.MapMouseEvent) {
      // don't send click event to map if measure tool is active
      if (measureStatus.isMeasuring === true) return;
      // check if we already have it
      const existingParcels = parcelDetails.filter((p) => booleanPointInPolygon(lngLat.toArray(), p.geom));
      if (existingParcels.length > 0) {
        // if existing, then select it
        existingParcels.forEach((p) => {
          if (store.selectedIds.has(p.uniqueId)) {
            store.selectedIds.delete(p.uniqueId);
          } else {
            store.selectedIds.add(p.uniqueId);
          }
        });
      } else {
        // get parcel details from server
        store.fetchParcelForPoint(projectId, lngLat.lng, lngLat.lat);
      }
    }
    map!.on('click', MAPBOX_PARCELS_LAYER_ID, onClick);
    return () => {
      map!.off('click', MAPBOX_PARCELS_LAYER_ID, onClick);
    };
  }, [map, parcelDetails]);

  const parcelLayer = useMemo(() => {
    return (
      <MapGL.Source
        id="landRightsDetails"
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: parcelDetails.map((p) => ({
            type: 'Feature',
            geometry: p.geom,
            properties: {
              label: p.i,
              // based on selection
              fillOpacity: selectedIds.has(p.uniqueId) ? 0.25 : 0,
            },
          })),
        }}
      >
        <MapGL.Layer
          source={'landRightsDetails'}
          type="fill"
          paint={{
            'fill-color': 'red',
            'fill-opacity': ['get', 'fillOpacity'],
          }}
        />
        <MapGL.Layer
          source={'landRightsDetails'}
          type="symbol"
          layout={{ 'text-field': ['get', 'label'], 'text-size': 20 }}
          paint={{
            'text-color': 'black',
            'text-halo-color': 'white',
            'text-halo-width': 20,
          }}
        />
      </MapGL.Source>
    );
  }, [parcelDetails, selectedIds]);

  // The tab needs parcels rendered while it is open: force it on and lock it (the picker
  // shows it checked + disabled), then return it to picker-controlled state on unmount.
  useTabControlledLayers(TAB_CONTROLLED_LAYER_IDS);

  return <>{parcelLayer}</>;
}
